import { useTranslation } from 'react-i18next';
import { Md } from '../components/Md';

function CodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <pre
      dir="ltr"
      className="mt-3 overflow-x-auto rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-xs leading-relaxed text-slate-300 md:text-sm"
    >
      <code>{children}</code>
    </pre>
  );
}

function ProviderSection({
  id,
  title,
  description,
  filename,
  yaml,
}: {
  id: string;
  title: string;
  description: string;
  filename: string;
  yaml: string;
}) {
  return (
    <section id={id} className="mt-10 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <p className="mt-2 text-sm text-slate-400">{description}</p>
      <p className="mt-4 font-mono text-xs text-cyan-400">{filename}</p>
      <CodeBlock>{yaml}</CodeBlock>
    </section>
  );
}

const githubYaml = `name: QuickTL

on:
  push:
    branches-ignore: [main]
  pull_request:
    types: [closed]

env:
  QUICKTL_API_KEY: \${{ secrets.QUICKTL_API_KEY }}

jobs:
  translate:
    if: github.event_name == 'push'
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: 20
      - run: npm install -g @quicktl/cli
      # actions/checkout@v4 checks out a detached HEAD, so git branch
      # auto-detection doesn't work — pass --branch explicitly.
      - name: Translate current branch
        run: quicktl translate --branch "\${{ github.ref_name }}"

  merge:
    if: >
      github.event_name == 'pull_request'
      && github.event.pull_request.merged == true
      && github.event.pull_request.base.ref == github.event.repository.default_branch
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: 20
      - run: npm install -g @quicktl/cli
      - name: Merge translations
        run: |
          quicktl merge \\
            --branch "\${{ github.event.pull_request.head.ref }}"
`;

const gitlabYaml = `stages:
  - translate
  - merge

# Add QUICKTL_API_KEY as a masked CI/CD variable in Settings → CI/CD → Variables.

.quicktl:
  image: node:20
  before_script:
    - npm install -g @quicktl/cli

translate:
  extends: .quicktl
  stage: translate
  rules:
    - if: $CI_COMMIT_BRANCH && $CI_COMMIT_BRANCH != $CI_DEFAULT_BRANCH
  # GitLab runners check out in detached HEAD by default, so git branch
  # auto-detection doesn't work — pass --branch explicitly.
  script:
    - quicktl translate --branch "$CI_COMMIT_BRANCH"

merge:
  extends: .quicktl
  stage: merge
  rules:
    - if: $CI_PIPELINE_SOURCE == "merge_request_event"
        && $CI_MERGE_REQUEST_TARGET_BRANCH_NAME == $CI_DEFAULT_BRANCH
      when: manual
  script:
    - quicktl merge --branch "$CI_MERGE_REQUEST_SOURCE_BRANCH_NAME"
`;

const bitbucketYaml = `image: node:20

# Add QUICKTL_API_KEY as a secured repository variable under
# Repository settings → Repository variables.

pipelines:
  branches:
    '{main}':
      - step:
          name: Merge translations
          script:
            - npm install -g @quicktl/cli
            # Extract the source branch from the merge commit message.
            - SOURCE_BRANCH=$(git log -1 --pretty=%s | sed -n 's/.*from \\([^ ]*\\).*/\\1/p')
            - quicktl merge --branch "$SOURCE_BRANCH"

  pull-requests:
    '**':
      - step:
          name: Translate branch
          script:
            - npm install -g @quicktl/cli
            - quicktl translate --branch "$BITBUCKET_BRANCH"
`;

export default function CIPage() {
  const { t } = useTranslation();

  const flowSteps = [
    { text: t('ci.flow.step1') },
    { text: t('ci.flow.step2') },
    { text: t('ci.flow.step3') },
    { text: t('ci.flow.step4') },
    {
      text: t('ci.flow.step5'),
      sub: (
        <>
          {t('ci.flow.manualNote')}{' '}
          <a href="#manual-review" className="text-cyan-400 hover:underline">
            {t('ci.flow.manualLink')}
          </a>
          .
        </>
      ),
    },
    { text: t('ci.flow.step6') },
    { text: t('ci.flow.step7') },
  ];

  return (
    <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
      <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
        {t('ci.heading')}
      </h1>
      <p className="mt-4 text-slate-400">{t('ci.description')}</p>

      {/* Table of contents */}
      <nav
        aria-label={t('ci.toc.heading')}
        className="mt-8 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6"
      >
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t('ci.toc.heading')}
        </h2>
        <ul className="mt-3 grid gap-2 text-sm text-cyan-400 sm:grid-cols-2">
          <li>
            <a href="#flow" className="hover:underline">
              {t('ci.toc.flow')}
            </a>
          </li>
          <li>
            <a href="#secrets" className="hover:underline">
              {t('ci.toc.secrets')}
            </a>
          </li>
          <li>
            <a href="#github" className="hover:underline">
              {t('ci.toc.github')}
            </a>
          </li>
          <li>
            <a href="#gitlab" className="hover:underline">
              {t('ci.toc.gitlab')}
            </a>
          </li>
          <li>
            <a href="#bitbucket" className="hover:underline">
              {t('ci.toc.bitbucket')}
            </a>
          </li>
          <li>
            <a href="#manual-review" className="hover:underline">
              {t('ci.toc.manual')}
            </a>
          </li>
        </ul>
      </nav>

      {/* Flow overview */}
      <section id="flow" className="mt-10 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
        <h2 className="font-display text-xl font-bold">{t('ci.flow.heading')}</h2>
        <p className="mt-2 text-sm text-slate-400">{t('ci.flow.description')}</p>
        <ol className="mt-4 space-y-3 text-sm text-slate-300">
          {flowSteps.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 font-mono text-[10px] leading-none text-emerald-400">
                {i + 1}
              </span>
              <div className="flex-1">
                <Md as="div">{step.text}</Md>
                {step.sub && (
                  <p className="mt-1 text-xs text-slate-400">{step.sub}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Secrets prerequisite */}
      <section id="secrets" className="mt-10 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
        <h2 className="font-display text-xl font-bold">{t('ci.secrets.heading')}</h2>
        <p className="mt-2 text-sm text-slate-400">{t('ci.secrets.description')}</p>
        <ul className="mt-4 space-y-1 text-sm text-slate-300">
          <li>
            <code className="font-mono text-cyan-400">QUICKTL_API_KEY</code>{' '}
            — <Md>{t('ci.secrets.apiKey')}</Md>
          </li>
        </ul>
        <p className="mt-3 text-sm text-slate-400">{t('ci.secrets.note')}</p>
      </section>

      <ProviderSection
        id="github"
        title={t('ci.github.heading')}
        description={t('ci.github.description')}
        filename=".github/workflows/quicktl.yml"
        yaml={githubYaml}
      />

      <ProviderSection
        id="gitlab"
        title={t('ci.gitlab.heading')}
        description={t('ci.gitlab.description')}
        filename=".gitlab-ci.yml"
        yaml={gitlabYaml}
      />

      <ProviderSection
        id="bitbucket"
        title={t('ci.bitbucket.heading')}
        description={t('ci.bitbucket.description')}
        filename="bitbucket-pipelines.yml"
        yaml={bitbucketYaml}
      />

      {/* Manual review */}
      <section
        id="manual-review"
        className="mt-14 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6"
      >
        <h2 className="font-display text-2xl font-bold">{t('ci.manual.heading')}</h2>
        <p className="mt-2 text-sm text-slate-400">{t('ci.manual.description')}</p>

        <h3 className="mt-6 font-display text-lg font-semibold">{t('ci.manual.dashboard.heading')}</h3>
        <p className="mt-2 text-sm text-slate-400">{t('ci.manual.dashboard.description')}</p>

        <h3 className="mt-6 font-display text-lg font-semibold">{t('ci.manual.tm.heading')}</h3>
        <Md as="p" className="mt-2 text-sm text-slate-400">
          {t('ci.manual.tm.description')}
        </Md>
        <div
          dir="ltr"
          className="mt-3 rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-xs text-slate-300 md:text-sm"
        >
          <span className="text-slate-500">$</span> quicktl tm set --source-locale en --target-locale fr
          --source "Cart" --translation "Panier"
        </div>
      </section>
    </div>
  );
}
